import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Paper, Stack } from '@mantine/core'
import { useTranslation } from 'react-i18next'

import { PAGE_STEP } from './constants'
import DocumentationMap from './DocumentationMap'
import DocumentationToolbar, { TIME_SEARCH_ID } from './DocumentationToolbar'
import Lightbox from './Lightbox'
import { timeJump } from './messages'
import MetadataPanel from './MetadataPanel'
import { metadataRows } from './metadata'
import PhotoStage from './PhotoStage'
import { playTick } from './playback'
import SplitDivider from './SplitDivider'
import type { LoadedArchive } from './useArchive'
import { useFrameNavigator } from './useFrameNavigator'
import { useLightboxImage } from './useLightboxImage'
import { usePlayback } from './usePlayback'
import { useShortcuts } from './useShortcuts'
import { useSplitPane } from './useSplitPane'

export type ViewerPreferences = { follow: boolean; satellite: boolean; splitHeight: number | null }

export const DEFAULT_VIEWER_PREFERENCES: ViewerPreferences = { follow: true, satellite: false, splitHeight: null }

const DocumentationViewer = ({
  archive,
  active,
  preferences,
  onPreferences,
  onLoadArchive,
}: {
  archive: LoadedArchive
  active: boolean
  preferences: ViewerPreferences
  onPreferences: (patch: Partial<ViewerPreferences>) => void
  onLoadArchive: () => void
}) => {
  const { t } = useTranslation('documentation')
  const { points } = archive
  const { follow, satellite, splitHeight } = preferences
  const [track, setTrack] = useState('')
  const [playing, setPlaying] = useState(false)
  const [query, setQuery] = useState('')
  const [hint, setHint] = useState({ text: '', miss: false })
  const [lightbox, setLightbox] = useState(false)

  const visible = useMemo(
    () => points.flatMap((point, index) => (!track || point.track === track ? [index] : [])),
    [points, track]
  )
  const tracks = useMemo(() => [...new Set(points.map(point => point.track).filter(Boolean))], [points])
  const { cursor, cursorRef, frame, select, stageRef } = useFrameNavigator(archive.file, points, visible)
  const selected = points[visible[cursor]] ?? null
  const setSplitHeight = useCallback((height: number) => onPreferences({ splitHeight: height }), [onPreferences])
  const split = useSplitPane(splitHeight, setSplitHeight)

  useEffect(() => {
    if (!active) setPlaying(false)
  }, [active])
  const fullImage = useLightboxImage(lightbox, archive.file, selected)
  const atStart = cursor <= 0
  const atEnd = cursor >= visible.length - 1

  const step = (offset: number) => void select(cursorRef.current + offset)
  const openLightbox = () => {
    if (points[visible[cursorRef.current]]?.image) setLightbox(true)
  }

  usePlayback(playing, async () => {
    const decision = playTick(cursorRef.current, visible.length)
    if (decision.action === 'stop') {
      setPlaying(false)
      return false
    }
    await select(decision.cursor)
    return true
  })

  useShortcuts(active, {
    previous: () => step(-1),
    next: () => step(1),
    back: () => step(-PAGE_STEP),
    forward: () => step(PAGE_STEP),
    first: () => void select(0),
    last: () => void select(visible.length - 1),
    togglePlay: () => setPlaying(value => !value),
    openLightbox,
    focusSearch: () => document.getElementById(TIME_SEARCH_ID)?.focus(),
    closeLightbox: () => setLightbox(false),
  })

  const jump = () => {
    const result = timeJump(
      t,
      query,
      selected?.time != null ? new Date(selected.time) : new Date(),
      visible.map(index => points[index].time)
    )
    setHint({ text: result.hint, miss: result.miss })
    if (result.position != null) void select(result.position)
  }

  return (
    <Box style={{ display: active ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <DocumentationToolbar
        cursor={cursor}
        length={visible.length}
        tracks={tracks}
        track={track}
        onTrack={setTrack}
        query={query}
        onQuery={setQuery}
        onJump={jump}
        hint={hint.text}
        hintMiss={hint.miss}
        playing={playing}
        onTogglePlay={() => setPlaying(value => !value)}
        follow={follow}
        onToggleFollow={() => onPreferences({ follow: !follow })}
        satellite={satellite}
        onSatellite={value => onPreferences({ satellite: value })}
        onSelect={position => void select(position)}
        onLoadArchive={onLoadArchive}
      />
      <Stack ref={split.workspaceRef} gap="xs" p="lg" style={{ flex: 1, minHeight: 0 }}>
        <Box
          h={splitHeight ?? undefined}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 4fr) minmax(16rem, 1fr)',
            gap: 'var(--mantine-spacing-lg)',
            flex: splitHeight == null ? '1 1 0' : 'none',
            minHeight: 0,
          }}
        >
          <Paper
            withBorder
            radius="md"
            p="md"
            style={{ minWidth: 0, minHeight: 0, overflow: 'hidden', display: 'flex' }}
          >
            <PhotoStage
              image={frame.image}
              message={frame.message}
              onPrev={() => step(-1)}
              onNext={() => step(1)}
              prevDisabled={atStart}
              nextDisabled={atEnd}
              onOpen={openLightbox}
              stageRef={node => {
                stageRef.current = node
              }}
            />
          </Paper>
          <MetadataPanel rows={metadataRows(selected, t('position'))} />
        </Box>
        <SplitDivider dividerRef={split.dividerRef} onPointerDown={split.onDividerDown} />
        <Paper withBorder radius="md" style={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden', position: 'relative' }}>
          <DocumentationMap
            points={points}
            visible={visible}
            cursor={cursor}
            follow={follow}
            satellite={satellite}
            onSelect={position => void select(position)}
          />
        </Paper>
      </Stack>
      {lightbox ? (
        <Lightbox
          point={selected}
          image={fullImage.image}
          preview={frame.featureIndex === visible[cursor] ? frame.image : null}
          error={fullImage.error}
          prevDisabled={atStart}
          nextDisabled={atEnd}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
          onClose={() => setLightbox(false)}
        />
      ) : null}
    </Box>
  )
}

export default DocumentationViewer
